import { Node, mergeAttributes } from "@tiptap/core";
import { NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps } from "@tiptap/react";
import { useTranslation } from "react-i18next";
import { Loader2, ImageOff } from "lucide-react";
import { useMedia } from "@/hooks/useMedia";
import { blurStyle } from "@/lib/mediaStyle";

/**
 * Custom Tiptap atom node that references an image from the `media` table by id.
 * Serializes to `<img data-media-id="...">` so the public web layer can resolve
 * the media object server-side. Replaces the legacy URL-based image extension.
 */
export const MediaNode = Node.create({
  name: "media-image",
  group: "block",
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      mediaId: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-media-id"),
        renderHTML: (attrs) => (attrs.mediaId ? { "data-media-id": attrs.mediaId as string } : {}),
      },
    };
  },

  parseHTML() {
    return [{ tag: "img[data-media-id]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["img", mergeAttributes(HTMLAttributes)];
  },

  addNodeView() {
    return ReactNodeViewRenderer(MediaNodeView);
  },
});

function MediaNodeView({ node, selected }: NodeViewProps) {
  const { t } = useTranslation();
  const mediaId = node.attrs.mediaId as string | null;

  const { data, isLoading, isError } = useMedia(mediaId);

  const media = data?.data;

  return (
    <NodeViewWrapper className="my-4">
      <div
        className={`relative overflow-hidden rounded-lg ${
          selected ? "outline outline-[3px] outline-offset-2 outline-blue-500" : ""
        }`}
      >
        {isLoading ? (
          <div className="flex h-48 w-full items-center justify-center bg-gray-100 text-gray-400 dark:bg-gray-800">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : isError || !media ? (
          <div className="flex h-48 w-full flex-col items-center justify-center gap-2 bg-gray-100 text-gray-400 dark:bg-gray-800">
            <ImageOff className="h-8 w-8" />
            <span className="text-xs">
              {t("media.editor.imageNotFound")}
              {mediaId ? ` (${mediaId})` : ""}
            </span>
          </div>
        ) : (
          <img
            src={media.url}
            alt={media.alt}
            className="h-auto max-w-full rounded-lg"
            style={blurStyle(media.blurDataUrl)}
          />
        )}
      </div>
    </NodeViewWrapper>
  );
}
